import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotify } from '@/components/snackbar';
import {
  BUSINESS_HEADER_REFUSED_EVENT,
  BUSINESS_HEADER_REFUSED_MESSAGE,
} from '@/consts/businessForbidden';
import { sortBusinessesForSwitcher } from '@/consts/businesses';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { BUSINESS_STORAGE_KEY, clearBusinessContext, setBusinessContext } from '@/http/axios';
import { PATH_DASHBOARD } from '@/routes/paths';
import { dismissMigrationBanner, getBusinesses } from '@/services/businesses';
import { CACHE_TTL } from '@/services/constants';
import { BACKGROUND_QUERY_META } from '@/utils/backgroundQuery';

export const BUSINESSES_QUERY_KEY = ['businesses'];

const BusinessContext = createContext({
  businesses: [],
  activeBusiness: null,
  isLoading: false,
  isError: false,
  isImpersonating: false,
  refetchBusinesses: () => {},
  switchBusiness: () => {},
  migrationBannerBusiness: null,
  dismissMigrationBanner: () => {},
  isDismissingMigrationBanner: false,
});

const readStoredBusinessId = () => {
  try {
    return localStorage.getItem(BUSINESS_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

export const BusinessProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const { impersonatedUserId } = useImpersonation();
  const isImpersonating = Boolean(impersonatedUserId);
  const notify = useNotify();

  // Espelho do armazenamento: lido dentro do cálculo do negócio ativo, ele não
  // recalcula quando a lista volta igual, e a volta ao padrão depois de uma recusa
  // ficaria presa no negócio recusado.
  const [storedId, setStoredId] = useState(readStoredBusinessId);

  const {
    data,
    isLoading,
    isError,
    refetch: refetchBusinesses,
  } = useQuery({
    queryKey: BUSINESSES_QUERY_KEY,
    queryFn: getBusinesses,
    staleTime: CACHE_TTL,
    retry: false,
    meta: BACKGROUND_QUERY_META,
  });

  const businesses = useMemo(() => data ?? [], [data]);

  // Sob impersonação o backend só devolve mais de um negócio ao colaborador da conta
  // (o suporte fica no padrão do dono): é ele que pode trocar.
  const isLockedToDefault = isImpersonating && businesses.length <= 1;

  const activeBusiness = useMemo(() => {
    if (!businesses.length) {
      return null;
    }

    const stored = businesses.find((business) => String(business.id) === String(storedId));

    return stored ?? businesses.find((business) => business.isDefault) ?? businesses[0];
  }, [businesses, storedId]);

  // O header de negócio vai em toda chamada desde o primeiro carregamento, com o
  // default da lista quando ainda não houve escolha (CBK-571). Também corrige um
  // id guardado que deixou de valer, como o de um negócio do qual a pessoa saiu.
  //
  // Sob impersonação nada é gravado: o armazenamento é do navegador, e quem
  // impersona costuma ser o suporte no próprio navegador, com a escolha dele lá.
  // O backend descarta o header nessa sessão, então o valor não muda a resposta.
  useEffect(() => {
    if (isLockedToDefault || !activeBusiness) {
      return;
    }

    if (String(storedId) !== String(activeBusiness.id)) {
      setBusinessContext(activeBusiness.id);
      setStoredId(String(activeBusiness.id));
    }
  }, [activeBusiness, isLockedToDefault, storedId]);

  // Escrita recusada porque o negócio guardado não é mais da pessoa (CBK-572): volta
  // ao padrão, recarrega a lista e avisa. A ação não é repetida; a leitura já caía no
  // padrão do lado do backend, então o que está na tela continua valendo.
  useEffect(() => {
    const onHeaderRefused = () => {
      clearBusinessContext();
      setStoredId(null);
      refetchBusinesses();
      notify.warning(BUSINESS_HEADER_REFUSED_MESSAGE);
    };

    window.addEventListener(BUSINESS_HEADER_REFUSED_EVENT, onHeaderRefused);

    return () => {
      window.removeEventListener(BUSINESS_HEADER_REFUSED_EVENT, onHeaderRefused);
    };
  }, [notify, refetchBusinesses]);

  const sortedBusinesses = useMemo(
    () => sortBusinessesForSwitcher(businesses, activeBusiness?.id ?? null),
    [businesses, activeBusiness]
  );

  const migrationBannerBusiness = useMemo(
    () => businesses.find((business) => business.showMigrationBanner) ?? null,
    [businesses]
  );

  // Esconde assim que o backend confirma, sem esperar um novo GET.
  const { mutate: dismissMigrationBannerMutation, isLoading: isDismissingMigrationBanner } =
    useMutation({
      mutationFn: dismissMigrationBanner,
      onSuccess: (_, { businessId }) => {
        queryClient.setQueryData(BUSINESSES_QUERY_KEY, (current) =>
          (current ?? []).map((business) =>
            String(business.id) === String(businessId)
              ? { ...business, showMigrationBanner: false }
              : business
          )
        );
      },
    });

  const handleDismissMigrationBanner = useCallback(() => {
    if (migrationBannerBusiness) {
      dismissMigrationBannerMutation({ businessId: migrationBannerBusiness.id });
    }
  }, [migrationBannerBusiness, dismissMigrationBannerMutation]);

  // RF-09: recarrega o contexto inteiro e volta para a Home, como a troca de
  // conta ja faz. Limpar so o cache do React Query deixaria estado local de
  // tela apontando para o negocio anterior.
  const switchBusiness = useCallback(
    (businessId) => {
      if (isLockedToDefault || !businessId || String(businessId) === String(activeBusiness?.id)) {
        return;
      }

      setBusinessContext(businessId);
      window.location.assign(PATH_DASHBOARD.general.home);
    },
    [activeBusiness, isLockedToDefault]
  );

  const value = useMemo(
    () => ({
      businesses: sortedBusinesses,
      activeBusiness,
      isLoading,
      isError,
      isImpersonating,
      isSwitchLocked: isLockedToDefault,
      refetchBusinesses,
      switchBusiness,
      migrationBannerBusiness,
      dismissMigrationBanner: handleDismissMigrationBanner,
      isDismissingMigrationBanner,
    }),
    [
      sortedBusinesses,
      activeBusiness,
      isLoading,
      isImpersonating,
      isLockedToDefault,
      isError,
      refetchBusinesses,
      switchBusiness,
      migrationBannerBusiness,
      handleDismissMigrationBanner,
      isDismissingMigrationBanner,
    ]
  );

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
};

BusinessProvider.propTypes = {
  children: PropTypes.node,
};

export const useBusiness = () => useContext(BusinessContext);

export default BusinessContext;
